import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router'

import { canWith, type AclAction } from '../acl/can'
import { isSoftwareCode, type Session } from '../stores/session'

import { getHomeRoute } from './home'
import { ROUTE_MAINTENANCE, ROUTE_NOT_AUTHORIZED } from './names'
import { notFoundLocation } from './not-found'
import { loginLocation, returnTarget } from './return-to'

/** The parts of a route location the guard reads (a resolved or normalised location both qualify). */
export interface GuardTarget {
  name?: RouteLocationNormalized['name'] | null
  params: RouteLocationNormalized['params']
  meta: RouteLocationNormalized['meta']
  /** The opened URL (path, query, hash) — carried to the login page (D-35). */
  fullPath?: string
}

export interface GuardContext {
  session: () => Session | null
  maintenanceMode: () => boolean
  can?: (session: Session, resource: string, action: AclAction) => boolean
  /** Called when the company-profile wizard gate redirects (the shell shows its danger toast). */
  onWizardRedirect?: () => void
}

function softwareOf(to: GuardTarget): string | null {
  const param = to.params.software
  if (typeof param === 'string' && param) return param
  const declared = to.meta.software
  return declared && declared !== 'common' ? declared : null
}

/**
 * One navigation decision, in the order D-21 fixes (ARCHITECTURE.md §3), preceded by the legacy maintenance switch
 * (wp/shell §4.11 step 1):
 *
 * 1. maintenance flag on → the maintenance page;
 * 2. public route → allowed (a signed-in user on a sign-in page goes home);
 * 3. no session → login, with the opened page as `?redirect=` (D-35, `return-to.ts`);
 * 4. software context: the route's software (the `:software` param, else `meta.software`) must be one of the user's
 *    softwares — an unknown code is a 404, a known one the user lacks is not-authorized (D-21), unless the route is
 *    shared with a software the user has (`meta.sharedWith`: an employee on engagement's survey page, queue #17);
 * 5. ACL: `meta.resource` / `meta.action` checked with `can()` → not-authorized when refused;
 * 6. allowed.
 */
export function resolveNavigation(to: GuardTarget, ctx: GuardContext): true | RouteLocationRaw {
  if (ctx.maintenanceMode() && to.name !== ROUTE_MAINTENANCE) return { name: ROUTE_MAINTENANCE }

  const session = ctx.session()
  if (to.meta.public) {
    return to.meta.redirectIfLoggedIn && session ? getHomeRoute(session) : true
  }

  if (!session) return loginLocation(to)

  const software = softwareOf(to)
  if (software !== null) {
    if (!isSoftwareCode(software)) return notFoundLocation(to)
    const has = (code: string) => session.softwares.some((entry) => entry.short === code)
    if (!has(software) && !(to.meta.sharedWith ?? []).some(has)) return { name: ROUTE_NOT_AUTHORIZED }
  }

  const resource = to.meta.resource
  if (resource) {
    const can = ctx.can ?? canWith
    if (!can(session, resource, to.meta.action ?? 'read')) return { name: ROUTE_NOT_AUTHORIZED }
  }

  return true
}

/**
 * The company-profile wizard gate (legacy `App.vue:136-153`, wp/shell §5.2): while the current workspace has an `Id`
 * and `WizzardCompleted !== 1`, any page whose path has a second segment other than `company-profile` sends the user to
 * `/<software>/company-profile`. One-segment paths (`/profile`, `/organizations`) are not gated, nor — new path, same
 * intent — another organisation's profile at `/organizations/:orgId` (legacy `/:software/company-profile/:orgId`).
 */
export function wizardRedirect(to: GuardTarget & { path: string }, session: Session | null): RouteLocationRaw | null {
  const workspace = session?.currentWorkspace
  if (!session || !workspace || workspace.WizzardCompleted === 1 || to.meta.public) return null
  const segments = to.path.split('/').filter(Boolean)
  if (segments.length < 2 || segments[1] === 'company-profile' || segments[0] === 'organizations') return null
  const fromPath = isSoftwareCode(segments[0]) ? segments[0] : null
  const software = fromPath ?? session.currentSoftware ?? 're'
  return { path: `/${software}/company-profile` }
}

export function installGuards(router: Router, ctx: GuardContext): void {
  router.beforeEach((to) => {
    // A signed-in user on a sign-in page that carries `?redirect=` (signed in in another tab) goes there, not home.
    if (to.meta.redirectIfLoggedIn && ctx.session() && !ctx.maintenanceMode()) {
      const target = returnTarget(router, to.query)
      if (target) return target
    }
    const decision = resolveNavigation(to, ctx)
    if (decision !== true) return decision
    const wizard = wizardRedirect(to, ctx.session())
    if (!wizard) return true
    ctx.onWizardRedirect?.()
    return wizard
  })
}
